import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { UserService } from '../services/user.service';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { User } from '../model/user';
import { getStoredUser } from '../helpers/user-storage.util';
import { openVersionConflictDialog } from '../helpers/version-conflict.util';
import { AuthenticationService } from '../services/authentication.service';
import { DeviceService, Device } from '../services/device.service';
import { ModalComponent } from '../shared/modal/modal.component';
import { OptInDialogComponent } from '../sms/opt-in-dialog/opt-in-dialog.component';
import { OptOutDialogComponent } from '../sms/opt-out-dialog/opt-out-dialog.component';

@Component({
  selector: 'app-settings',
  standalone: true,
  providers: [provideNativeDateAdapter()],
  imports: [
    CommonModule,
    MatDatepickerModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    MatSlideToggleModule,
    MatDialogModule,
    ModalComponent,
    OptInDialogComponent,
    OptOutDialogComponent,
  ],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss',
})
export class SettingsComponent implements OnDestroy {
  passwordRegx: RegExp = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

  user: User = {};
  outOfOfficeStartDate: Date | null = null;
  outOfOfficeEndDate: Date | null = null;
  editOutOfOfficeToggle: boolean = false;
  allNotifiedByEnabled: boolean = false;
  allInvitationNotificationsEnabled: boolean = false;
  allProposalNotifcationsEnabled: boolean = false;
  allActiveProjectNotificationsEnabled: boolean = false;
  showCurrentPassword: boolean = false;
  showNewPassword: boolean = false;
  showConfirmPassword: boolean = false;
  showOptInDialog: boolean = false;
  showOptOutDialog: boolean = false;
  isSubmittingPassword = false;
  isSavingSettings = false;

  // Security section (Keep Me Signed In device management).
  devices: Device[] = [];
  isLoadingDevices = false;
  // True only when a refresh failed while we already had devices to show — the
  // list on screen may be out of date. Never set on a first/empty load, so the
  // never-enabled path stays clean (see loadDevices()).
  devicesStale = false;
  // INTERNAL staff are structurally ineligible for trusted sessions (the trust
  // endpoint 403s them), so the "keep me signed in" toggle is hidden for them.
  // A missing/empty role defaults to PROFESSIONAL_ASSOCIATE, matching RoleGuard
  // and the post-login KMSI nudge.
  isInternalUser = false;
  showRevokeModal = false;
  deviceToRevoke: Device | null = null;
  showLogoutAllModal = false;
  // A password change still in flight when the user leaves is cancelled, so its
  // answer cannot act for whoever is signed in by the time it arrives. (On 1.0 the
  // answer's fresh token is stored here; Connect 2.0's session is an httpOnly
  // cookie, so there is no token to store.)
  private destroy$ = new Subject<void>();

  protected newPassword = new FormGroup(
    {
      currentPassword: new FormControl('', [Validators.required]),
      newPassword: new FormControl('', [
        Validators.required,
        Validators.pattern(this.passwordRegx),
      ]),
      confirmNewPassword: new FormControl('', [
        Validators.required,
        Validators.pattern(this.passwordRegx),
      ]),
    },
    { validators: this.customPasswordMatching.bind(this) }
  );

  constructor(
    private userService: UserService,
    private authService: AuthenticationService,
    private deviceService: DeviceService,
    private toastr: ToastrService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.user = getStoredUser();
    this.loadDevices();
    this.populateFieldsFromUser();
  }

  // Populates every local field from `this.user` — the ngOnInit prefill, and also
  // re-run after a reload following a version-conflict dialog (see
  // `reloadUserAfterConflict`) so the toggles reflect the current authoritative
  // record before the vendor re-applies their change.
  private populateFieldsFromUser(): void {
    const role = this.user.role || 'PROFESSIONAL_ASSOCIATE';
    this.isInternalUser = role === 'INTERNAL';
    this.outOfOfficeStartDate = this.user.outOfOfficeStartDate
      ? new Date(this.user.outOfOfficeStartDate)
      : null;

    this.outOfOfficeEndDate = this.user.outOfOfficeEndDate
      ? new Date(this.user.outOfOfficeEndDate)
      : null;
    this.editOutOfOfficeToggle = Boolean(
      this.outOfOfficeStartDate && this.outOfOfficeEndDate
    );

    this.allNotifiedByEnabled =
      (this.user.notifyByInApp &&
        this.user.notifyByEmail &&
        ((!this.user.phone && !this.user.phone2) || this.user.notifyBySms)) ||
      false;
    this.allInvitationNotificationsEnabled =
      (this.user.newInvitationNotification &&
        this.user.projectUpdateNotification &&
        this.user.cancelledInvitationNotification) ||
      false;
    this.allProposalNotifcationsEnabled =
      (this.user.awardedNotification &&
        this.user.closedNotification &&
        this.user.cancelledProposalNotification) ||
      false;
    this.allActiveProjectNotificationsEnabled =
      (this.user.revisionRequestedNotification &&
        this.user.completedNotification &&
        this.user.cancelledActiveProjectNotification) ||
      false;
  }

  toggleCurrentPasswordVisibility() {
    this.showCurrentPassword = !this.showCurrentPassword;
  }

  toggleNewPasswordVisibility() {
    this.showNewPassword = !this.showNewPassword;
  }

  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  toggleOptInDialog() {
    this.showOptInDialog = !this.showOptInDialog;
  }

  toggleOptOutDialog() {
    this.showOptOutDialog = !this.showOptOutDialog;
  }

  trimCurrentPassword() {
    let password = this.newPassword.get('currentPassword')?.value;
    if (password) {
      this.newPassword.patchValue({
        currentPassword: password.trim(),
      });
    }
  }

  trimNewPassword() {
    let password = this.newPassword.get('newPassword')?.value;
    if (password) {
      this.newPassword.patchValue({
        newPassword: password.trim(),
      });
    }
  }

  trimConfirmPassword() {
    let password = this.newPassword.get('confirmNewPassword')?.value;
    if (password) {
      this.newPassword.patchValue({
        confirmNewPassword: password.trim(),
      });
    }
  }

  public customPasswordMatching(
    control: AbstractControl
  ): ValidationErrors | null {
    const password = control.get('newPassword')?.value;
    const confirmPassword = control.get('confirmNewPassword')?.value;

    return password === confirmPassword
      ? null
      : { passwordMismatchError: true };
  }

  editOutOfOffice() {
    this.editOutOfOfficeToggle = !this.editOutOfOfficeToggle;
    if (this.editOutOfOfficeToggle === false) {
      this.outOfOfficeStartDate = null;
      this.outOfOfficeEndDate = null;
      this.handleUpdateUserOutOfOfficeDates();
    }
  }

  saveOutOfOffice() {
    // Check if both start and end dates are provided
    if (this.outOfOfficeStartDate && this.outOfOfficeEndDate) {
      // Validate that the end date is greater than the start date
      if (
        new Date(this.outOfOfficeEndDate) > new Date(this.outOfOfficeStartDate)
      ) {
        // Proceed with saving
        this.handleUpdateUserOutOfOfficeDates();
        // this.editOutOfOfficeToggle = !this.editOutOfOfficeToggle;
      } else {
        // If the end date is not greater than the start date, show an error
        this.toastr.info('End date must be after the start date.');
      }
    } else {
      // If either of the dates is missing
      this.toastr.info('Please select both start and end dates.');
    }
  }

  handleUpdateUserOutOfOfficeDates() {
    let userPayload = this.user;
    userPayload.outOfOfficeStartDate = this.outOfOfficeStartDate;
    userPayload.outOfOfficeEndDate = this.outOfOfficeEndDate;
    this.userService
      .updateUserOutOfOfficeDates(userPayload)
      .subscribe((apiResponse) => {
        if (apiResponse.status === 'FAILURE') {
          this.toastr.error('An error occurred. Please try again.');
        }
        if (apiResponse.code !== 200) {
          this.toastr.error('An error occurred. Please try again.');
        } else if (apiResponse.code === 200) {
          this.toastr.success(apiResponse.message);
          this.user = { ...this.user, ...apiResponse.data };
          localStorage.setItem('user', JSON.stringify(this.user));
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  saveNewPassword() {
    if (this.newPassword.invalid) {
      this.newPassword.markAllAsTouched();
      return;
    } else if (this.newPassword.valid) {
      this.isSubmittingPassword = true;
      this.authService
        .newPassword(
          this.user.email ?? '',
          this.newPassword.value.currentPassword ?? '',
          this.newPassword.value.newPassword ?? ''
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (data) => {
            this.isSubmittingPassword = false;
            if (data.status === 'SUCCESS') {
              this.toastr.success(data.message);
              this.newPassword.reset({
                currentPassword: '',
                newPassword: '',
                confirmNewPassword: '',
              });
            } else {
              this.toastr.error(data.message);
            }
          },
          error: () => {
            this.isSubmittingPassword = false;
            this.toastr.error('An error occurred. Please try again.');
          }
        });
    }
  }

  saveNotifiedBy() {
    this.updateUser(this.user);
  }

  saveInvitationNotifications() {
    this.updateUser(this.user);
  }

  saveProposalNotifications() {
    this.updateUser(this.user);
  }

  saveActiveProjectNotifications() {
    this.updateUser(this.user);
  }

  handleSelectAllNotifiedBy() {
    this.user = {
      ...this.user,
      notifyByEmail: this.allNotifiedByEnabled,
      notifyByInApp: this.allNotifiedByEnabled,
      notifyBySms:
        ((this.user.phone || this.user.phone2) && this.allNotifiedByEnabled) ||
        false,
    };
  }

  handleNotifiedByChange() {
    this.allNotifiedByEnabled =
      (this.user.notifyByInApp &&
        this.user.notifyByEmail &&
        (this.user.phone || this.user.phone2) &&
        this.user.notifyBySms) ||
      false;
  }

  optInToSms(smsUser: any) {
    this.userService
      .optInUserToSmsNotification({ ...smsUser, notifyBySms: true })
      .subscribe((apiResponse) => {
        if (apiResponse.status === 'FAILURE') {
          if (apiResponse.code === 409) {
            this.showVersionConflictDialog();
          } else {
            // The consent update still runs and bumps the version even when the text itself
            // fails to send, so the persisted record must be adopted here too — otherwise the
            // cached version goes stale and the next opt-in/opt-out is rejected as a conflict
            // nobody caused.
            this.user = { ...this.user, ...apiResponse.data };
            localStorage.setItem('user', JSON.stringify(this.user));
            this.toastr.error(apiResponse.message);
          }
        } else {
          this.toastr.success(apiResponse.message);
          this.user = { ...this.user, ...apiResponse.data };
          localStorage.setItem('user', JSON.stringify(this.user));
        }
      });
    this.toggleOptInDialog();
  }

  optOutFromSms(smsUser: any) {
    this.userService
      .optOutUserFromSmsNotification(smsUser)
      .subscribe((apiResponse) => {
        if (apiResponse.status === 'FAILURE') {
          if (apiResponse.code === 409) {
            this.showVersionConflictDialog();
          } else {
            // Same shape as opt-in: the guarded update still lands and bumps the version on a
            // send failure, so adopt the returned record here too.
            this.user = { ...this.user, ...apiResponse.data };
            localStorage.setItem('user', JSON.stringify(this.user));
            this.toastr.error(apiResponse.message);
          }
        } else {
          this.toastr.success(apiResponse.message);
          this.user = { ...this.user, ...apiResponse.data };
          localStorage.setItem('user', JSON.stringify(this.user));
        }
      });
    this.toggleOptOutDialog();
  }

  handleSelectAllInvitationNotifications() {
    this.user = {
      ...this.user,
      newInvitationNotification: this.allInvitationNotificationsEnabled,
      projectUpdateNotification: this.allInvitationNotificationsEnabled,
      cancelledInvitationNotification: this.allInvitationNotificationsEnabled,
    };
  }

  handleInvitationNotificationChange() {
    this.allInvitationNotificationsEnabled =
      (this.user.newInvitationNotification &&
        this.user.projectUpdateNotification &&
        this.user.cancelledInvitationNotification) ||
      false;
  }

  handleSelectAllProposalNotifications() {
    this.user = {
      ...this.user,
      awardedNotification: this.allProposalNotifcationsEnabled,
      closedNotification: this.allProposalNotifcationsEnabled,
      cancelledProposalNotification: this.allProposalNotifcationsEnabled,
    };
  }

  handleProposalNotificationChange() {
    this.allProposalNotifcationsEnabled =
      (this.user.awardedNotification &&
        this.user.closedNotification &&
        this.user.cancelledProposalNotification) ||
      false;
  }

  handleSelectAllActiveProjectNotifications() {
    this.user = {
      ...this.user,
      revisionRequestedNotification: this.allActiveProjectNotificationsEnabled,
      completedNotification: this.allActiveProjectNotificationsEnabled,
      cancelledActiveProjectNotification:
        this.allActiveProjectNotificationsEnabled,
    };
  }

  handleActiveProjectNotificationChange() {
    this.allActiveProjectNotificationsEnabled =
      (this.user.revisionRequestedNotification &&
        this.user.completedNotification &&
        this.user.cancelledActiveProjectNotification) ||
      false;
  }

  private updateUser(user: User) {
    this.isSavingSettings = true;
    this.userService.updateUser(user).subscribe({
      next: (response: any) => {
        this.isSavingSettings = false;
        const apiResponse = response;
        if (apiResponse.status === 'FAILURE') {
          if (apiResponse.code === 409) {
            this.showVersionConflictDialog();
          } else if (apiResponse.message) {
            this.toastr.error(apiResponse.message);
          } else {
            this.toastr.error('An error occurred. Please try again.');
          }
        } else {
          const updatedUser = response.data;
          if (updatedUser) {
            this.user = { ...this.user, ...updatedUser };
            localStorage.setItem('user', JSON.stringify(this.user));
            this.toastr.success(apiResponse.message);
          } else {
            this.toastr.error('An error occurred. Please try again.');
          }
        }
      },
      error: () => {
        this.isSavingSettings = false;
        this.toastr.error('An error occurred. Please try again.');
      }
    });
  }

  // A `409` FAILURE envelope means another writer changed this record since it was
  // loaded (the optimistic-lock guard rejected a stale `version`) — this page holds
  // its user for the component's lifetime, so retrying the same save would conflict
  // again. Show the reload-and-reapply dialog instead of a generic error toast; the
  // reload runs once the vendor acknowledges it (it's `disableClose`, so
  // acknowledging is the only way to close it).
  private showVersionConflictDialog(): void {
    openVersionConflictDialog(this.dialog).subscribe(() =>
      this.reloadUserAfterConflict()
    );
  }

  // Refetches the authoritative profile and re-populates every field from it, so the
  // vendor sees the current values — including the version that just rejected their
  // save — before re-applying their change.
  private reloadUserAfterConflict(): void {
    this.authService.me().subscribe({
      next: (user) => {
        this.user = user;
        this.populateFieldsFromUser();
      },
      error: () =>
        this.toastr.error(
          'Failed to reload your settings. Please refresh the page.'
        )
    });
  }

  // ---------------------------------------------------------------------------
  // Security — Keep Me Signed In device management
  // ---------------------------------------------------------------------------

  /** Whether the CURRENT session is already a trusted device. */
  get currentDeviceTrusted(): boolean {
    return this.devices.some((d) => d.isCurrent);
  }

  /**
   * Awareness path B: offer "keep me signed in on this device" only when the
   * current session is not yet trusted AND the user is eligible (non-INTERNAL).
   */
  get showTrustToggle(): boolean {
    return (
      !this.isInternalUser &&
      !this.isLoadingDevices &&
      !this.currentDeviceTrusted
    );
  }

  loadDevices(): void {
    this.isLoadingDevices = true;
    this.devicesStale = false;
    this.deviceService.listDevices().subscribe({
      next: (devices) => {
        this.devices = devices ?? [];
        this.isLoadingDevices = false;
      },
      error: () => {
        // Two failure shapes, two treatments — keyed on whether we already have
        // a list, since a failed response can't tell us if devices exist:
        //  * Nothing loaded yet (the common case — arrived from the login nudge
        //    having never enabled KMSI): degrade SILENTLY to the enable toggle.
        //    A "retry" prompt is a dead end when there's nothing to load.
        //  * We already had devices (a refresh after revoke/trust, or a real
        //    outage for a device-having user): KEEP the known list on screen and
        //    add a subtle "couldn't refresh" hint — here a retry is meaningful.
        // Errors on user-initiated actions (revoke/logout-all/trust) still toast.
        this.isLoadingDevices = false;
        this.devicesStale = this.devices.length > 0;
      },
    });
  }

  /** Coarse relative time for "Last active" (e.g. "2 days ago"). */
  formatLastActive(lastUsedAt: string): string {
    if (!lastUsedAt) {
      return '—';
    }
    const then = new Date(lastUsedAt).getTime();
    if (isNaN(then)) {
      return '—';
    }
    const seconds = Math.floor((Date.now() - then) / 1000);
    if (seconds < 45) {
      return 'just now';
    }
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
    }
    const days = Math.floor(hours / 24);
    if (days < 30) {
      return days === 1 ? '1 day ago' : `${days} days ago`;
    }
    const months = Math.floor(days / 30);
    if (months < 12) {
      return months === 1 ? '1 month ago' : `${months} months ago`;
    }
    const years = Math.floor(days / 365);
    return years === 1 ? '1 year ago' : `${years} years ago`;
  }

  trustThisDevice(): void {
    this.deviceService.trustCurrentDevice().subscribe({
      next: () => {
        this.toastr.success('This device is now trusted.');
        // Refresh so the current device appears (trusted) — no re-login.
        this.loadDevices();
      },
      error: () => {
        this.toastr.error('An error occurred. Please try again.');
      },
    });
  }

  askRevokeDevice(device: Device): void {
    this.deviceToRevoke = device;
    this.showRevokeModal = true;
  }

  cancelRevokeDevice(): void {
    this.showRevokeModal = false;
    this.deviceToRevoke = null;
  }

  confirmRevokeDevice(): void {
    const device = this.deviceToRevoke;
    this.showRevokeModal = false;
    if (!device) {
      return;
    }
    this.deviceService.revokeDevice(device.deviceId).subscribe({
      next: () => {
        if (device.isCurrent) {
          // Revoking the current device ends this session — hard logout.
          this.authService.logout();
        } else {
          this.toastr.success('Device removed.');
          this.loadDevices();
        }
        this.deviceToRevoke = null;
      },
      error: () => {
        this.toastr.error('An error occurred. Please try again.');
        this.deviceToRevoke = null;
      },
    });
  }

  askLogoutAllDevices(): void {
    this.showLogoutAllModal = true;
  }

  cancelLogoutAllDevices(): void {
    this.showLogoutAllModal = false;
  }

  confirmLogoutAllDevices(): void {
    this.showLogoutAllModal = false;
    this.deviceService.logoutAllDevices().subscribe({
      next: () => {
        // Logging out of all devices ends this session too — hard logout, same
        // as revoking the current device (clears client caches so the next
        // sign-in on this tab doesn't inherit stale user data).
        this.authService.logout();
      },
      error: () => {
        this.toastr.error('An error occurred. Please try again.');
      },
    });
  }
}
